// NotificationsSettingsPage — Per-channel notification preferences
// Layout: hero w/ master switch, sections per channel, granular controls

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Toggle {
  key: string;
  label: string;
  sub: string;
  push: boolean;
  email: boolean;
  sms: boolean;
  inApp: boolean;
}

const TOGGLES: Toggle[] = [
  { key: 'trades',   label: 'Trade activity',     sub: 'Open, close, fills, alerts',         push: true,  email: false, sms: false, inApp: true },
  { key: 'bot',      label: 'Bot status',         sub: 'Start, stop, errors, daily summary',  push: true,  email: true,  sms: false, inApp: true },
  { key: 'challenge',label: 'Challenge progress', sub: 'Target hit, drawdown warnings',       push: true,  email: true,  sms: true,  inApp: true },
  { key: 'payouts',  label: 'Payouts',            sub: 'Sent, failed, scheduled',             push: true,  email: true,  sms: false, inApp: true },
  { key: 'news',     label: 'News & market',      sub: 'Breaking news, scheduled events',     push: false, email: true,  sms: false, inApp: true },
  { key: 'promo',    label: 'Promotions & tips',  sub: 'Offers, product updates, academy',    push: false, email: false, sms: false, inApp: false },
  { key: 'security', label: 'Security alerts',    sub: 'Logins, password changes, suspicious', push: true, email: true,  sms: true,  inApp: true },
];

export function NotificationsSettingsPage() {
  const nav = useNavigate();
  const [masterEnabled, setMasterEnabled] = useState(true);
  const [quietHours, setQuietHours] = useState(true);
  const [data, setData] = useState(TOGGLES);
  const [weekly, setWeekly] = useState(true);

  function toggle(key: string, channel: 'push' | 'email' | 'sms' | 'inApp') {
    setData((curr) => curr.map((t) => t.key === key ? { ...t, [channel]: !t[channel] } : t));
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Notifications</div>
        <div className="w-9" />
      </div>

      {/* Master toggle */}
      <div className="mx-4 mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue-tint text-tf-blue text-[18px]">🔔</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-tf-text-1">Allow notifications</div>
            <div className="text-[11px] text-tf-text-3">Master switch</div>
          </div>
          <Switch value={masterEnabled} onChange={setMasterEnabled} testid="master-toggle" />
        </div>
      </div>

      {/* Channel header */}
      <div className="mx-4 mt-3 grid grid-cols-4 gap-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-tf-text-3 text-center">
        <div></div>
        <div>Push</div>
        <div>Email</div>
        <div>SMS</div>
      </div>

      {/* Categories */}
      <div className={['mx-4 mt-1 overflow-hidden rounded-[16px] border border-tf-border bg-white', !masterEnabled ? 'opacity-50 pointer-events-none' : ''].join(' ')}>
        {data.map((t, i) => (
          <div key={t.key} data-testid={`notif-${t.key}`} className={['px-4 py-3', i < data.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
            <div className="grid grid-cols-4 items-center gap-1">
              <div className="pr-2">
                <div className="text-[14px] font-semibold text-tf-text-1">{t.label}</div>
                <div className="text-[11px] text-tf-text-3 truncate">{t.sub}</div>
              </div>
              <div className="flex justify-center"><Switch value={t.push}  onChange={() => toggle(t.key, 'push')}  small testid={`${t.key}-push`} /></div>
              <div className="flex justify-center"><Switch value={t.email} onChange={() => toggle(t.key, 'email')} small testid={`${t.key}-email`} /></div>
              <div className="flex justify-center"><Switch value={t.sms}   onChange={() => toggle(t.key, 'sms')}   small testid={`${t.key}-sms`} /></div>
            </div>
          </div>
        ))}
      </div>

      {/* Quiet hours */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Quiet hours</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-purple-tint text-tf-info text-[18px]">🌙</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-tf-text-1">Do not disturb</div>
            <div className="text-[11px] text-tf-text-3">10:00 PM - 7:00 AM · all channels</div>
          </div>
          <Switch value={quietHours} onChange={setQuietHours} testid="quiet-toggle" />
        </div>
      </div>

      {/* Weekly summary */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Digests</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-green-tint text-tf-profit text-[18px]">📊</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-tf-text-1">Weekly performance summary</div>
            <div className="text-[11px] text-tf-text-3">Every Monday · 9:00 AM</div>
          </div>
          <Switch value={weekly} onChange={setWeekly} testid="weekly-toggle" />
        </div>
        <button className="flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition" data-testid="test-notification">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-orange-tint text-tf-warning text-[18px]">🧪</div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[14px] font-semibold text-tf-text-1">Send test notification</div>
            <div className="text-[11px] text-tf-text-3">Verify all channels work</div>
          </div>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
    </div>
  );
}

function Switch({ value, onChange, small, testid }: { value: boolean; onChange: (v: boolean) => void; small?: boolean; testid?: string }) {
  return (
    <button
      onClick={() => onChange(!value)}
      data-testid={testid}
      className={['relative rounded-full transition-colors', small ? 'h-6 w-10' : 'h-7 w-12', value ? 'bg-tf-profit' : 'bg-tf-surface-3'].join(' ')}
    >
      <div className={['absolute top-0.5 rounded-full bg-white shadow-md transition-all', small ? 'h-5 w-5' : 'h-6 w-6', value ? (small ? 'left-[18px]' : 'left-[22px]') : 'left-0.5'].join(' ')} />
    </button>
  );
}
